// src/components/Weather/WeatherDetailsPage.js
import React, { useState, useEffect, useContext } from "react"; // UPDATED: Added useContext
import { useTranslation } from "react-i18next";
import axios from "axios"; // NEW: Added axios for API calls
import { UserContext } from "../../UserContext"; // NEW: Added UserContext to get user/farm info
import { getWeatherData } from "./weatherService"; // UPDATED: Switched to the live service

// Imports for icons (no changes)
import {
  WiSunrise,
  WiSunset,
  WiStrongWind,
  WiHumidity,
  WiBarometer,
  WiDaySunny,
  WiTornado,
  WiCloud,
  WiRaindrop,
} from "react-icons/wi";
import { FaEye } from "react-icons/fa";

// Imports for charts (no changes)
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

// Imports for map (no changes)
import { MapContainer, TileLayer, Polygon, useMap } from "react-leaflet";

import "./weather.css";

// Helper component to automatically fit the map to the given polygon bounds
const AutoFitBounds = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length > 0) {
      map.fitBounds(bounds, { padding: [10, 10] });
    }
  }, [bounds, map]);
  return null;
};

// NEW: Helper function to parse the location string from your API
const parseCustomLocationString = (locationString) => {
  if (!locationString || typeof locationString !== "string") return [];
  const pairs = locationString.split(/(?<=\d)-/g);
  return pairs
    .map((pair) => pair.split(",").map(Number))
    .filter(
      (coords) =>
        Array.isArray(coords) &&
        coords.length === 2 &&
        !isNaN(coords[0]) &&
        !isNaN(coords[1])
    );
};

// NEW: Helper function to calculate the center of the polygon for accurate weather
const calculateCentroid = (boundaries) => {
  if (!boundaries || boundaries.length === 0) return { lat: 0, lon: 0 };
  const center = boundaries.reduce(
    (acc, curr) => [
      acc[0] + curr[0] / boundaries.length,
      acc[1] + curr[1] / boundaries.length,
    ],
    [0, 0]
  );
  return { lat: center[0], lon: center[1] };
};

const WeatherDetailsPage = () => {
  const { t, i18n } = useTranslation();
  const { userData, token } = useContext(UserContext); // NEW: Get user data and token

  const [activeTab, setActiveTab] = useState("overview");

  const [fields, setFields] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [weatherData, setWeatherData] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // UPDATED: useEffect hook to fetch fields from your EGRO CARE API
  useEffect(() => {
    // Wait for user data and token to be available
    if (!userData?.Farm?.id || !token) {
      setLoading(false); // If no user data, stop loading
      return;
    }

    const fetchFields = async () => {
      setLoading(true);
      setError("");
      try {
        const farmId = userData.Farm.id;
        // Fetches the list of fields for the current farm
        const response = await axios.get(`api/field/findByFarmId/${farmId}`, {
          headers: { Authorization: token },
        });

        const fetchedFields = response.data.items.map((field) => {
          // Parse the location string from the API into boundaries for the map
          field.boundaries = parseCustomLocationString(field.location);
          return field;
        });

        setFields(fetchedFields);
        if (fetchedFields.length > 0) {
          setSelectedField(fetchedFields[0]); // Select the first field by default
        } else {
          setError(t("No fields found for this farm."));
          setLoading(false);
        }
      } catch (err) {
        console.error("Failed to fetch fields:", err);
        setError(t("Failed to load field data. Please try again."));
        setLoading(false);
      }
    };

    fetchFields();
  }, [userData, token, t]);

  // UPDATED: useEffect hook to fetch weather based on the selected field's location
  useEffect(() => {
    if (!selectedField) return;

    const fetchWeather = async () => {
      setLoading(true);
      setError("");

      const { lat, lon } = calculateCentroid(selectedField.boundaries);

      try {
        const response = await getWeatherData(lat, lon, i18n.language);
        setWeatherData(response.data);
      } catch (err) {
        console.error("Failed to fetch weather data:", err);
        setError(
          t(
            "Failed to load weather data. Please check your API key and try again."
          )
        );
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
  }, [selectedField, i18n.language, t]);

  const handleFieldChange = (e) => {
    const field = fields.find((f) => f.id === parseInt(e.target.value));
    setSelectedField(field);
  };

  // UPDATED: Improved loading/error states
  if (!userData || fields.length === 0) {
    if (loading)
      return <div className="dashboard-message">{t("Loading Fields...")}</div>;
    if (error) return <div className="dashboard-message error">{error}</div>;
    return <div className="dashboard-message">{t("No fields available.")}</div>;
  }

  if (loading || !weatherData || !selectedField) {
    return (
      <div className="dashboard-message">
        {t("Loading Weather for")} {selectedField?.name}...
      </div>
    );
  }

  if (error && !weatherData) {
    return <div className="dashboard-message error">{error}</div>;
  }

  // The rest of the component JSX remains the same, as the UI is already correct
  return (
    <div className="weather-details-page">
      <div className="weather-header">
        <h1>
          {t("Weather in")} {selectedField.name}
        </h1>
        <select
          className="field-selector"
          value={selectedField.id}
          onChange={handleFieldChange}
        >
          {fields.map((field) => (
            <option key={field.id} value={field.id}>
              {field.name}
            </option>
          ))}
        </select>
      </div>

      <div className="current-weather-card">
        <img
          src={`https://openweathermap.org/img/wn/${weatherData.current.weather[0].icon}@4x.png`}
          alt="weather icon"
        />
        <div className="current-weather-info">
          <p className="current-temp">
            {Math.round(weatherData.current.temp)}°C
          </p>
          <p className="current-desc">
            {weatherData.current.weather[0].description}
          </p>
          <p className="current-feels-like">
            {t("Feels like")} {Math.round(weatherData.current.feels_like)}°
          </p>
        </div>
      </div>

      <div className="weather-tabs">
        <button
          className={`weather-tab ${activeTab === "overview" ? "active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          {t("Now")}
        </button>
        <button
          className={`weather-tab ${activeTab === "hourly" ? "active" : ""}`}
          onClick={() => setActiveTab("hourly")}
        >
          {t("Hourly")}
        </button>
        <button
          className={`weather-tab ${activeTab === "daily" ? "active" : ""}`}
          onClick={() => setActiveTab("daily")}
        >
          {t("7 Days")}
        </button>
      </div>

      <div className="weather-content">
        {activeTab === "overview" && (
          <WeatherOverviewTab
            data={weatherData.current}
            field={selectedField}
          />
        )}
        {activeTab === "hourly" && (
          <WeatherHourlyTab data={weatherData.hourly.slice(0, 25)} />
        )}
        {activeTab === "daily" && (
          <WeatherDailyTab data={weatherData.daily.slice(0, 8)} />
        )}
      </div>
    </div>
  );
};

// --- Tab Components ---

const WeatherOverviewTab = ({ data, field }) => {
  const { t } = useTranslation();
  return (
    <>
      <SunriseSunset data={data} />
      <div className="overview-grid">
        <OverviewCard
          icon={<WiHumidity />}
          title={t("Humidity")}
          value={`${data.humidity}%`}
        />
        <OverviewCard
          icon={<WiBarometer />}
          title={t("Pressure")}
          value={`${data.pressure} ${t("hPa")}`}
        />
        <OverviewCard
          icon={<WiStrongWind />}
          title={t("Wind Speed")}
          value={`${data.wind_speed.toFixed(2)} ${t("m/s")}`}
        />
        <OverviewCard
          icon={<FaEye />}
          title={t("Visibility")}
          value={`${data.visibility / 1000} ${t("km")}`}
        />
        <OverviewCard
          icon={<WiDaySunny />}
          title={t("UV Index")}
          value={data.uvi.toFixed(2)}
        />
        <OverviewCard
          icon={<WiTornado />}
          title={t("Wind Gust")}
          value={`${(data.wind_gust || 0).toFixed(2)} ${t("m/s")}`}
        />
        <OverviewCard
          icon={<WiCloud />}
          title={t("Cloud Cover")}
          value={`${data.clouds}%`}
        />
        <OverviewCard
          icon={<WiRaindrop />}
          title={t("Dew Point")}
          value={`${Math.round(data.dew_point)}°C`}
        />
      </div>

      <div className="map-section">
        <MapContainer
          center={[field.lat, field.lon]}
          attributionControl={false}
          scrollWheelZoom={false}
          dragging={false}
          doubleClickZoom={false}
          zoomControl={false}
          touchZoom={false}
          keyboard={false}
          boxZoom={false}
          style={{ height: "300px", width: "100%" }}
        >
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            attribution="&copy; Esri"
          />
          <Polygon
            positions={field.boundaries}
            pathOptions={{ color: "#c7356c", dashArray: "5, 5" }}
          />
          <AutoFitBounds bounds={field.boundaries} />
        </MapContainer>
      </div>
    </>
  );
};

// Replace the existing WeatherHourlyTab component with this complete version

const WeatherHourlyTab = ({ data }) => {
  const { t, i18n } = useTranslation();

  // Prepare data for both charts
  const chartData = data.map((hour) => {
    const locale = i18n.language === "ar" ? "ar-EG" : "en-US";

    return {
      time: new Date(hour.dt * 1000).toLocaleTimeString(locale, {
        hour: "numeric",
        minute: "2-digit",
        hour12: i18n.language === "ar", // ص / م for Arabic, AM/PM for English
      }),
      temp: Math.round(hour.temp),
      precipitation: Math.round(hour.pop * 100),
      icon: `https://openweathermap.org/img/wn/${hour.weather[0].icon}.png`,
    };
  });

  // Custom Tooltip for the Temperature Chart
  const CustomTempTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <img src={payload[0].payload.icon} alt="weather icon" />
          <p className="tooltip-label">{`${label}`}</p>
          <p className="tooltip-value">{`${payload[0].value}°C`}</p>
        </div>
      );
    }
    return null;
  };

  // Custom Tooltip for the Precipitation Chart
  const CustomPrecipTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip precip-tooltip">
          <p className="tooltip-label">{`${label}`}</p>
          <p className="tooltip-value">{`${payload[0].value}%`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    // Render two separate sections, one for each chart
    <>
      <div className="forecast-section">
        <h3>{t("Temperature over 24 Hours")}</h3>
        <div style={{ width: "100%", height: 300 }}>
          <ResponsiveContainer>
            <AreaChart
              data={chartData}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="colorTemp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8884d8" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="time" tick={{ fill: "#666", fontSize: 12 }} />
              <YAxis tick={{ fill: "#666", fontSize: 12 }} unit="°C" />
              <Tooltip content={<CustomTempTooltip />} />
              <Area
                type="monotone"
                dataKey="temp"
                stroke="#8884d8"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorTemp)"
                dot={{ stroke: "#8884d8", strokeWidth: 2, r: 4, fill: "#fff" }}
                activeDot={{ r: 6, fill: "#8884d8" }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="forecast-section">
        <h3>{t("Chance of Precipitation")}</h3>
        <div style={{ width: "100%", height: 300 }}>
          <ResponsiveContainer>
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <XAxis dataKey="time" tick={{ fill: "#666", fontSize: 12 }} />
              <YAxis
                tick={{ fill: "#666", fontSize: 12 }}
                unit="%"
                domain={[0, 100]}
              />
              <Tooltip content={<CustomPrecipTooltip />} />
              <Line
                type="monotone"
                dataKey="precipitation"
                stroke="#d9534f" // Reddish color to match reference
                strokeWidth={2}
                dot={{ stroke: "#d9534f", strokeWidth: 2, r: 4, fill: "#fff" }}
                activeDot={{ r: 6, fill: "#d9534f" }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
};

// Replace the existing WeatherDailyTab component with this one

const WeatherDailyTab = ({ data }) => {
  const { t, i18n } = useTranslation();

  const next7Days = data.slice(0, 7);

  const formatDate = (timestamp, index) => {
    return new Date(timestamp * 1000).toLocaleDateString(i18n.language, {
      weekday: "long",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="forecast-section">
      <h3>{t("Week's Climate")}</h3>
      <div className="daily-forecast-list-updated">
        {next7Days.map((day, index) => (
          <div key={index} className="daily-row-updated">
            <div className="daily-date-col">{formatDate(day.dt, index)}</div>
            <div className="daily-condition-col">
              <img
                src={`https://openweathermap.org/img/wn/${day.weather[0].icon}@2x.png`}
                alt={day.weather[0].description}
              />
              <div className="condition-text">
                <p className="condition-desc">{day.weather[0].description}</p>
                <p className="condition-humidity">
                  {day.humidity}% <WiHumidity />
                </p>
              </div>
            </div>
            <div className="daily-temp-col">
              <span>{Math.round(day.temp.max)}°</span> /{" "}
              <span>{Math.round(day.temp.min)}°</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// --- Reusable UI Components ---
const OverviewCard = ({ icon, title, value }) => (
  <div className="overview-card">
    <div className="overview-card-icon">{icon}</div>
    <div className="overview-card-content">
      <p className="overview-card-title">{title}</p>
      <p className="overview-card-value">{value}</p>
    </div>
  </div>
);

const SunriseSunset = ({ data }) => {
  const { t, i18n } = useTranslation();
  const sunrise = data.sunrise;
  const sunset = data.sunset;
  const now = data.dt;
  const totalDaylight = sunset - sunrise;
  const elapsed = now - sunrise;
  const progress = Math.max(0, Math.min(100, (elapsed / totalDaylight) * 100));

  return (
    <div className="sunrise-sunset-card">
      <div className="sun-times">
        <span>
          {i18n.language === "ar" ? (
            <>
              <WiSunset />
              {t("Sunset")}{" "}
              {new Date(sunset * 1000).toLocaleTimeString("ar-EG", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              })}
            </>
          ) : (
            <>
              <WiSunrise /> {t("Sunrise")}{" "}
              {new Date(sunrise * 1000).toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              })}
            </>
          )}
        </span>

        <span>
          {i18n.language === "ar" ? (
            <>
              <WiSunrise />
              {t("Sunrise")}{" "}
              {new Date(sunrise * 1000).toLocaleTimeString("ar-EG", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              })}
            </>
          ) : (
            <>
              <WiSunset /> {t("Sunset")}{" "}
              {new Date(sunset * 1000).toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              })}
            </>
          )}
        </span>
      </div>
      <div className="sun-progress-bar">
        <div className="sun-progress" style={{ width: `${progress}%` }}></div>
      </div>
    </div>
  );
};

export default WeatherDetailsPage;
